import { ClassNames, useTheme } from '@emotion/react';
import { CheckOne, CloseOne, Right } from '@icon-park/react';
import cn from 'classnames';
import { noop } from 'lodash';
import {
  Children,
  cloneElement,
  type MemoExoticComponent,
  type MouseEvent,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
} from 'react';
import { Collapse } from 'react-collapse';
import type { To } from 'react-router-dom';

import { ComponentSwitch, useMemoizedBundle } from '@didaskill/ui/utils';

import { LinearProgress } from '../../../data-display/linear-progress/LinearProgress';
import { ICON_SIZES } from '../../../general/icon/constants';
import { Typography } from '../../../general/typography/Typography';
import { Link } from '../../link/Link';
import { SUB_SECTION_PROGRESS_STATE } from '../sub-section/constants';
import { SECTION_STATES, SECTION_VARIANTS } from './constants';
import { TableOfContentsSectionContext } from './TableOfContentsSection.context';
import * as styles from './TableOfContentsSection.styles';

export type TableOfContentsSectionProps = {
  /** Children (table of content sub section items) */
  children?: ReactNode;
  /** Left icon */
  icon?: ReactElement;
  /** Section label. */
  label?: ReactNode;
  /** Link href. */
  href?: To;
  /** Text display below label. */
  subLabel?: ReactNode;
  /** Is the TableOfContents section item currently selected. */
  isSelected?: boolean;
  /** Current state of the section. */
  state?: SECTION_STATES;
  /** Color theme variant. */
  variant?: SECTION_VARIANTS;
  /** Is the section currently open. Always use with onToggleOpen. */
  isOpen?: boolean;
  /** Toggle open state. Always use with isOpen. */
  onToggleOpen?: (event: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Section progress as a percentage (from 0 to 100).
   * Only visible if `progressState` is not `NONE`.
   */
  progress?: number;

  /**
   * Progress state of the section.
   * Controls the appearance and visibility of the progress bar.
   */
  progressState?: SUB_SECTION_PROGRESS_STATE;
  /** Click event handler. */
  onClick?: () => void;
};

export const TableOfContentsSectionBase = ({
  children = null,
  icon = null,
  label = null,
  href = null,
  subLabel = null,
  isSelected = false,
  state = SECTION_STATES.NONE,
  variant = SECTION_VARIANTS.PRIMARY,
  isOpen = false,
  onToggleOpen = noop,
  progress = null,
  progressState = SUB_SECTION_PROGRESS_STATE.NONE,
  onClick = noop,
}: TableOfContentsSectionProps) => {
  const theme = useTheme();

  const hasSectionState = state !== SECTION_STATES.NONE;

  const [iconBackgroundColor, ...iconColors] = styles.getIconFills(
    theme,
    variant,
    hasSectionState,
    isSelected,
  );

  const contextValue = useMemoizedBundle({ variant });

  const canExpand = !!Children.toArray(children).filter(Boolean).length;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <TableOfContentsSectionContext.Provider value={contextValue}>
      <li>
        <div
          role="button"
          css={styles.header}
          className={cn(variant, {
            isSelected,
          })}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {icon && !hasSectionState && (
            <div
              css={styles.icon(theme, iconBackgroundColor)}
              className={cn({
                isSelected,
              })}
            >
              <div>
                {cloneElement(icon, {
                  size: ICON_SIZES.LARGE,
                  fill: iconColors,
                })}
              </div>
            </div>
          )}
          <ComponentSwitch value={state}>
            <ComponentSwitch.Item value={SECTION_STATES.COMPLETED}>
              <CheckOne
                size={ICON_SIZES.LARGE}
                fill={[theme.colors.success[500], theme.colors.success[50]]}
              />
            </ComponentSwitch.Item>
            <ComponentSwitch.Item value={SECTION_STATES.UNCOMPLETED}>
              <CloseOne
                size={ICON_SIZES.LARGE}
                fill={[theme.colors.danger[500], theme.colors.danger[50]]}
              />
            </ComponentSwitch.Item>
          </ComponentSwitch>

          <Link to={href} isDisabled={!href} css={styles.link}>
            <div css={styles.label}>
              {!!subLabel && (
                <Typography variant={Typography.VARIANTS.BUTTON_SMALL_REGULAR}>
                  {subLabel}
                </Typography>
              )}

              <Typography variant={Typography.VARIANTS.BUTTON_LARGE_REGULAR}>{label}</Typography>
              {Number.isFinite(progress) && progressState !== SUB_SECTION_PROGRESS_STATE.NONE && (
                <LinearProgress
                  css={styles.progress}
                  progress={progress}
                  backgroundColor={
                    progressState === SUB_SECTION_PROGRESS_STATE.IN_PROGRESS
                      ? theme.colors.primary[25]
                      : theme.colors.danger[400]
                  }
                  progressColor={
                    progressState === SUB_SECTION_PROGRESS_STATE.IN_PROGRESS
                      ? theme.colors.primary[400]
                      : theme.colors.success[500]
                  }
                />
              )}
            </div>
          </Link>

          {canExpand && (
            <button
              type="button"
              onClick={onToggleOpen}
              css={styles.arrow}
              className={cn({ isOpen })}
            >
              <Right size={12} />
            </button>
          )}
        </div>

        {canExpand && (
          <ClassNames>
            {({ css }) => (
              <Collapse
                isOpened={isOpen}
                theme={{
                  collapse: css`
                    transition: ${theme.transitions.default('height')};
                  `,
                }}
              >
                <ul css={styles.list}>{children}</ul>
              </Collapse>
            )}
          </ClassNames>
        )}
      </li>
    </TableOfContentsSectionContext.Provider>
  );
};

TableOfContentsSectionBase.displayName = 'TableOfContentsSection';

export const TableOfContentsSection: MemoExoticComponent<typeof TableOfContentsSectionBase> & {
  STATES?: typeof SECTION_STATES;
  VARIANTS?: typeof SECTION_VARIANTS;
} = memo(TableOfContentsSectionBase);

TableOfContentsSection.STATES = SECTION_STATES;
TableOfContentsSection.VARIANTS = SECTION_VARIANTS;
